import { debounce } from "lodash";
import * as React from "react";
import { Keys } from "~/common/enums";
import { DEFAULT_FOCUS_POINT } from "./DefaultFocusPoint";

const FOCUSABLE_ELEMENTS = [
    "a[href]:not([disabled])",
    'input:not([disabled]):not([type="hidden"])',
    "select:not([disabled])",
    "textarea:not([disabled])",
    "button:not([disabled])",
    '[tabindex]:not([disabled]):not([tabindex="-1"]',
].join(",");

export interface FocusTrapProps {
    autoFocus?: boolean;
    enforceFocus?: boolean;
    /**
     * if set then focus trap tries to return focus back
     * when focus was lost due to active element removal from dom.
     * When active element is removed from dom then browsers silently
     * set focus to body.
     * So if flag is set then component will check (after events that migth lead
     * to element detaching from dom such as mouse click and keyboard events)
     * if active element is body and if yes then restore focus back to first
     * focusable element inside this component
     */
    handleFocusedElementUnmounting?: boolean;
    className?: string;
    style?: React.CSSProperties;
    containerRef?: React.RefObject<HTMLElement>;
    onKeyUp?: (event: React.KeyboardEvent) => any;
}

/**
 * This component tries to keep focus inside it's container when user
 * navigates on elements by pressing tab/tab + shift.
 *
 * Also might set focus on mount to the first focusable element inside container (see autoFocus prop)
 */
export class FocusTrap extends React.Component<FocusTrapProps> {
    static defaultProps: Partial<FocusTrapProps> = {
        autoFocus: true,
        enforceFocus: true,
        handleFocusedElementUnmounting: false,
    };

    _containerElement: HTMLDivElement;

    setContainerRef = (element: HTMLDivElement) => {
        if (this.props.containerRef) {
            // @ts-ignore
            this.props.containerRef.current = element;
        }

        this._containerElement = element;
    };

    private checkThatFocusInside = debounce(() => {
        if (!document.activeElement || document.activeElement === document.body) {
            this.bringFocusInsideContainer();
        }
    }, 100);

    getNodeRemovalHandlers(): React.HTMLAttributes<HTMLDivElement> {
        if (!this.props.handleFocusedElementUnmounting) {
            return {};
        }

        const mouseHandler: React.MouseEventHandler = e => {
            this.checkThatFocusInside();
        };

        return {
            // use capture phase here because we often call stopPropagation on events.
            onMouseUpCapture: mouseHandler,
            onMouseDownCapture: mouseHandler,
            onKeyDownCapture: this.checkThatFocusInside,
            onKeyUpCapture: this.checkThatFocusInside,
        };
    }

    render() {
        const nodeRemovalHandleres = this.getNodeRemovalHandlers();

        return (
            <div
                ref={this.setContainerRef}
                className={this.props.className}
                onKeyDown={this.handleKeydown}
                onKeyUp={this.props.onKeyUp}
                style={this.props.style}
                {...nodeRemovalHandleres}
            >
                {this.props.children}
            </div>
        );
    }

    handleKeydown = (e: React.KeyboardEvent<any>) => {
        const shouldProcess = this.props.enforceFocus && e.key === Keys.Tab && this._containerElement;

        if (!shouldProcess) {
            return;
        }

        // set focus to the last element if user press tab + shift on the first focusable element
        // and set focus to the first element if user press tab on the last focusable element.

        const nodes = this._containerElement.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS),
            firstEl = nodes[0],
            lastEl = nodes[nodes.length - 1],
            currentEl = e.target;
        let targetEl: HTMLElement = null;

        const isNotTabbable = this.isDefaultFocusPoint(currentEl as Element);

        if ((isNotTabbable || currentEl === firstEl) && e.shiftKey) {
            targetEl = lastEl;
        } else if ((isNotTabbable || currentEl === lastEl) && !e.shiftKey) {
            targetEl = firstEl;
        }

        if (targetEl) {
            e.preventDefault();
            targetEl.focus();
        }
    };

    componentDidMount() {
        if (this.props.autoFocus) {
            this.bringFocusInsideContainer();
        }
    }

    bringFocusInsideContainer() {
        // set focus with requestAnimationFrame because
        // focusing might lead to additional repaint and blinks on ui.
        // for details see bug 34993:IE11: My Assignments, customization control blinks on opening
        requestAnimationFrame(() => {
            if (!this._containerElement) {
                return;
            }

            // do nothing if focus already in container.
            if (
                document.activeElement &&
                this._containerElement.contains(document.activeElement) &&
                !this.isDefaultFocusPoint(document.activeElement)
            ) {
                return;
            }

            const node = this._containerElement.querySelector(FOCUSABLE_ELEMENTS) as HTMLElement;
            if (node) {
                node.focus();
            }
        });
    }

    isDefaultFocusPoint(element: Element) {
        return element.className === DEFAULT_FOCUS_POINT;
    }
}
